import React, { useEffect, useMemo, useState } from 'react';
import { ExpectedListService } from '../services/api';
import { encryptExpectedList } from '../services/expectedListCrypto';
import { parseClipboard, pasteInto, validateRows, toPayload, expandShortYear } from '../services/expectedListRows';
import { loadLocal, saveLocal, forgetLocal } from '../services/expectedListStorage';
import { isUnitStarted } from '../services/teamAccess';
import './ExpectedChildren.css';

const newRow = () => ({ id: crypto.randomUUID(), lastName: '', firstName: '', birthDate: '', parentEmail: '' });
const isFilled = (r) => r.lastName || r.firstName || r.birthDate || r.parentEmail;
const COLUMNS = [['lastName', 'Nom'], ['firstName', 'Prénom'], ['birthDate', 'Date de naissance (JJ/MM/AAAA)'], ['parentEmail', 'Email d\'un parent (facultatif)']];

const ExpectedChildren = ({ unit, receivedCount }) => {
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(null);         // liste déjà envoyée (serveur)
    const [local, setLocal] = useState(null);       // copie locale
    // Dérivé de la prop : suit le rafraîchissement de l'unité sans recharger la page.
    const noKey = !isUnitStarted(unit);
    const [status, setStatus] = useState('');
    const [sending, setSending] = useState(false);
    const unitStart = unit.start_dt.split('T')[0];
    const unitEnd = unit.end_dt.split('T')[0];

    useEffect(() => {
        const l = loadLocal(unit.id);
        setLocal(l);
        setRows(l?.rows?.length ? l.rows : [newRow()]);
        ExpectedListService.getMeta(unit.id).then(setMeta).catch(() => setStatus('Impossible de lire la liste envoyée.'));
    }, [unit.id]);

    const filled = useMemo(() => rows.filter(isFilled), [rows]);
    const { errors, duplicates, valid } = useMemo(() => validateRows(filled, unitStart), [filled, unitStart]);
    const errorLines = rows.map((r, i) => [i + 1, errors[r.id]]).filter(([, e]) => e);
    const localIsCurrent = meta && local && local.version === meta.list_version;

    const send = async () => {
        if (meta && !localIsCurrent && !window.confirm(
            `La liste envoyée le ${new Date(meta.updated_at).toLocaleDateString('fr-FR')} (${meta.count} enfants) n'est pas disponible sur cet ordinateur. Ce nouvel envoi la remplacera entièrement. Continuer ?`
        )) return;
        setSending(true); setStatus('');
        try {
            const pem = await ExpectedListService.getPublicKey(unit.id);
            const { blob, key } = await encryptExpectedList(toPayload(filled), pem);
            const { list_version } = await ExpectedListService.send(unit.id, { blob, key, count: filled.length });
            const data = { rows: filled, sentAt: new Date().toISOString(), version: list_version, unitEndDate: unitEnd };
            saveLocal(unit.id, data); setLocal(data);
            setMeta({ count: filled.length, list_version, updated_at: data.sentAt });
            setStatus(`Liste envoyée : ${filled.length} enfants.`);
        } catch (e) {
            setStatus(e.response?.status === 409
                ? 'Un membre de l\'équipe doit d\'abord rejoindre le groupe avec l\'app.'
                : 'L\'envoi a échoué. Réessayez.');
        } finally { setSending(false); }
    };

    const forget = () => {
        if (!window.confirm('Oublier la liste sur cet ordinateur ? Elle reste envoyée à l\'équipe.')) return;
        forgetLocal(unit.id); setLocal(null); setRows([newRow()]);
    };

    const update = (id, field, value) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

    // Un collage de plusieurs cellules (tableur) remplit le tableau à partir de la cellule active.
    // Une date en année courte (JJ/MM/AA) est réécrite en JJ/MM/AAAA.
    const onPaste = (e, row, col) => {
        const text = e.clipboardData.getData('text');
        const matrix = parseClipboard(text);
        if (!matrix) {
            if (COLUMNS[col][0] !== 'birthDate' || expandShortYear(text) === text) return;
            e.preventDefault();
            update(rows[row].id, 'birthDate', expandShortYear(text.trim()));
            return;
        }
        e.preventDefault();
        if (matrix.length) setRows((rs) => pasteInto(rs, row, col, matrix, newRow));
    };

    // Flèches haut/bas et Entrée : passer à la même colonne de la ligne voisine.
    const onKeyDown = (e, row, col) => {
        const delta = e.key === 'ArrowDown' || (e.key === 'Enter' && !e.shiftKey) ? 1 : e.key === 'ArrowUp' || (e.key === 'Enter' && e.shiftKey) ? -1 : 0;
        if (!delta) return;
        const next = e.currentTarget.closest('table').querySelector(`[data-cell="${row + delta}-${col}"]`);
        if (next) { e.preventDefault(); next.focus(); next.select(); }
    };

    return (
        <div className="expected-children">
            <h4>Enfants attendus</h4>
            {meta && <p className="expected-summary">{meta.count} attendus · {receivedCount} fiches reçues · liste envoyée le {new Date(meta.updated_at).toLocaleDateString('fr-FR')}</p>}
            {noKey && <p className="expected-warning">Un membre de l'équipe doit d'abord rejoindre le groupe avec l'app.</p>}
            {meta && !localIsCurrent && <p className="expected-warning">La liste envoyée n'est pas disponible sur cet ordinateur (ou a été modifiée ailleurs). Un nouvel envoi la remplacera entièrement.</p>}
            {local && <p className="expected-info">Liste enregistrée sur cet ordinateur. <button type="button" className="btn-outline" onClick={forget}>Oublier la liste sur cet ordinateur</button></p>}
            <p className="expected-help">Dans votre tableur, copiez les colonnes Nom, Prénom, Date de naissance et, si vous voulez pouvoir relancer les parents depuis l'app, Email d'un parent (facultatif), avec ou sans ligne d'en-tête, puis collez-les (Ctrl/Cmd + V) dans la première cellule du tableau. Flèches haut/bas et Entrée pour changer de ligne.</p>
            <p className="expected-help">Les emails sont chiffrés avec la liste : notre serveur ne les lit pas. Ils ne lui sont transmis que lorsque l'équipe appuie sur « Relancer les parents » dans l'app, pour envoyer le message de relance.</p>
            <div className="expected-grid-wrapper">
            <table className="expected-grid">
                <thead><tr><th />{COLUMNS.map(([, label]) => <th key={label}>{label}</th>)}<th /></tr></thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.id} className={duplicates.includes(r.id) ? 'expected-duplicate' : ''}>
                            <td className="expected-rownum">{i + 1}</td>
                            {COLUMNS.map(([field], c) => (
                                <td key={field}>
                                    <input
                                        data-cell={`${i}-${c}`}
                                        value={r[field] ?? ''}
                                        onChange={(e) => update(r.id, field, e.target.value)}
                                        onPaste={(e) => onPaste(e, i, c)}
                                        onKeyDown={(e) => onKeyDown(e, i, c)}
                                        className={errors[r.id]?.[field] ? 'expected-error' : ''}
                                    />
                                </td>
                            ))}
                            <td><button type="button" aria-label="Supprimer la ligne" onClick={() => setRows((rs) => rs.length > 1 ? rs.filter((x) => x.id !== r.id) : [newRow()])}>Supprimer</button></td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
            <div className="expected-actions">
                <button type="button" className="btn-outline" onClick={() => setRows((rs) => [...rs, newRow()])}>Ajouter une ligne</button>
            </div>
            {errorLines.length > 0 && (
                <ul className="expected-errors">
                    {errorLines.map(([n, e]) => <li key={n}>Ligne {n} : {Object.values(e).join(' · ')}</li>)}
                </ul>
            )}
            {duplicates.length > 0 && <p className="expected-warning">{duplicates.length} doublon(s) signalé(s) en orange.</p>}
            <p>{filled.length} enfant(s), {errorLines.length} ligne(s) en erreur.</p>
            <button type="button" className="btn-primary btn-send" disabled={sending || noKey || !valid || filled.length === 0} onClick={send}>
                {sending ? 'Envoi…' : 'Envoyer la liste'}
            </button>
            {status && <p className="expected-summary" role="status" style={{ marginTop: '0.75rem' }}>{status}</p>}
        </div>
    );
};

export default ExpectedChildren;
